import * as React from 'react';
import { Transition } from 'react-transition-group';

import { VALID_ICONS } from '../../../Icon/SVGIcon';
import { IActionButtonProps } from '../ActionButton/ActionButton';
import { VALID_TRANSITION_STATES } from '../../Animations/Animations';
import { isOutsideClick, watchKeyboardClose } from '../../Utils/events/events';
import { useTransitionEnded } from '../../Utils/Hooks/events/useTransitionEnded';
import { useEventListener } from '../../Utils/Hooks/events/useEventListener';
import { VALID_DIMENSION_DATA } from '../../Utils/Styles/calcDimensionStyles';
import { ScreenReaderOnly } from '../../Accessibility/ScreenReaderOnly';

import { Btn, Container, DropdownContent } from './DropdownButtonStyles';

const DURATION = 150;
interface IDropdownButtonProps extends IActionButtonProps {
  ariaControls: string;
  closedIcon?: VALID_ICONS;
  contentWidth?: VALID_DIMENSION_DATA;
  buttonText: string;
  expandedIcon?: VALID_ICONS;
  expanded?: boolean;
  srText?: string;
  dropdownPosition?: 'right' | 'left';
}

const DropdownButton = React.forwardRef<HTMLButtonElement, IDropdownButtonProps>((
  {
    ariaControls,
    buttonText,
    children,
    contentWidth,
    expanded = false,
    variant,
    iconProps,
    selectedIconProps,
    dropdownPosition,
    srText,
  },
  ref
) => {
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = React.useState(expanded);
  const {contentHasTransitioned, transitionEndListener} = useTransitionEnded(isExpanded);

  const setRef = React.useCallback(
    node => {
      if (node !== null) {
        contentRef.current = node;
      }
    },
    []
  );

  const handleToggle = () => { setIsExpanded(!isExpanded); };
  const close = () => { setIsExpanded(false); };

  useEventListener('keydown', watchKeyboardClose(contentRef, close));

  useEventListener(
    'click',
    (e: Event) => {
      const clickIsOutside = isExpanded && contentHasTransitioned && isOutsideClick(contentRef, e as MouseEvent);
      if (clickIsOutside) {
        close();
      }
    }
  );

  return (
    <Container>
      <Btn
        selected={isExpanded}
        onClick={handleToggle}
        aria-expanded={isExpanded ? isExpanded : undefined}
        aria-controls={ariaControls ? ariaControls : undefined}
        variant={variant || 'action'}
        iconProps={iconProps}
        selectedIconProps={selectedIconProps}
        ref={ref}
      >
        <ScreenReaderOnly>{srText}</ScreenReaderOnly>
        {buttonText}
      </Btn>
      <Transition
        in={isExpanded}
        timeout={DURATION}
        addEndListener={transitionEndListener}
      >
        {(state: VALID_TRANSITION_STATES) => (
          state === VALID_TRANSITION_STATES.exited ? null : (
            <DropdownContent
              position={dropdownPosition}
              transitionState={state}
              id={ariaControls}
              ref={setRef}
              widthProp={contentWidth}
            >
              {children}
            </DropdownContent>
          )
        )}
      </Transition>
    </Container>
  );
});

export default DropdownButton;
